import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["alerts", "alertStore", "onAlertClose", "use", "style"],
  _excluded2 = ["id", "message", "titleText", "type"];
import { useCallback, useEffect, useState, useRef } from 'react';
import { Layer } from '../Layer/Layer';
import { FloatingAlertStore } from './FloatingAlertStore';
import memoizeOne from 'react-utils/memoizeOne';
import { callIfPossible } from '../../utils/functions/functions';
import FloatingAlert from '../FloatingAlert/FloatingAlert';
import partial from 'react-utils/partial';
import { AlertListTransition } from './AlertListTransition';
import styled from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { URANUS_LAYER } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_TOP_OFFSET = 20;
const NAV_BAR_ELEMENT_ID_V4 = 'hs-nav-v4';
const FLOATING_ALERT_SUBSCRIPTION_KEY = 'FloatingAlertList';
let subscriptionKeyId = 0;
const sortAlerts = (alertsFromProps, alertsFromStore) => [...alertsFromProps, ...alertsFromStore].sort((a, b) => {
  return a.timestamp - b.timestamp;
});
const subscribeToAlertStore = (subscriptionKey, setAlertsFromStore, alertStore) => {
  alertStore.subscribe(subscriptionKey, alertsFromStore => {
    setAlertsFromStore(alertsFromStore);
  });
};
const unsubscribeFromAlertStore = (subscriptionKey, alertStore) => {
  alertStore.unsubscribe(subscriptionKey);
};
const getStyle = (style, topOffset) => Object.assign({}, style, {
  top: topOffset
});
const OuterWrapper = styled.div.withConfig({
  displayName: "FloatingAlertList__OuterWrapper",
  componentId: "sc-1uryz54-0"
})(({
  $cssStyles,
  $use
}) => {
  return Object.assign({
    display: 'block',
    left: 0,
    position: $use === 'contextual' ? 'relative' : 'fixed',
    textAlign: 'center',
    top: 0,
    width: '100%',
    zIndex: URANUS_LAYER,
    pointerEvents: 'none'
  }, $cssStyles);
});
const Wrapper = styled.div.withConfig({
  displayName: "FloatingAlertList__Wrapper",
  componentId: "sc-1uryz54-1"
})(({
  theme
}) => {
  return {
    left: 0,
    overflow: 'auto',
    position: 'absolute',
    top: 0,
    width: '100%',
    '& > *:not(:first-child)': {
      marginTop: theme.space['400']
    }
  };
});
export const FloatingAlertList = _ref => {
  let {
      alerts = [],
      alertStore = FloatingAlertStore,
      onAlertClose,
      use = 'fullscreen',
      style
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [alertsFromStore, setAlertsFromStore] = useState([]);
  const [topOffset, setTopOffset] = useState(null);
  const subscriptionKeyRef = useRef(`${FLOATING_ALERT_SUBSCRIPTION_KEY}-${subscriptionKeyId++}`);
  const _getStyle = memoizeOne(getStyle);
  const updateContainerOffset = useCallback(() => {
    const {
      scrollTop
    } = document.documentElement;
    const navElement = document.getElementById(NAV_BAR_ELEMENT_ID_V4);
    let navHeight = 0;
    if (navElement) {
      navHeight = navElement.clientHeight;
    }
    const maxTopOffset = DEFAULT_TOP_OFFSET + navHeight;
    if (scrollTop > navHeight) {
      setTopOffset(DEFAULT_TOP_OFFSET);
    } else {
      setTopOffset(maxTopOffset - scrollTop);
    }
  }, []);
  useEffect(() => {
    const subscriptionKey = subscriptionKeyRef.current;
    if (use !== 'contextual') {
      window.addEventListener('scroll', updateContainerOffset);
      updateContainerOffset();
    }
    subscribeToAlertStore(subscriptionKey, setAlertsFromStore, alertStore);
    return () => {
      if (use !== 'contextual') {
        window.removeEventListener('scroll', updateContainerOffset);
      }
      unsubscribeFromAlertStore(subscriptionKey, alertStore);
    };
  }, [alertStore, updateContainerOffset, use]);
  const handleAlertClose = useCallback((alert, evt) => {
    callIfPossible(alert.onClose, evt, alert);
    callIfPossible(onAlertClose, evt, alert);
  }, [onAlertClose]);
  const renderedAlerts = sortAlerts(alerts, alertsFromStore).map(alert => {
    const {
        id,
        message,
        titleText,
        type
      } = alert,
      alertRest = _objectWithoutPropertiesLoose(alert, _excluded2);
    return /*#__PURE__*/_jsx(FloatingAlert, Object.assign({
      onClose: partial(handleAlertClose, alert),
      titleText: titleText,
      type: type
    }, alertRest, {
      children: message
    }), id);
  });
  const computedStyle = _getStyle(style, topOffset);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const renderedAlertList = /*#__PURE__*/_jsx(OuterWrapper, Object.assign({
    $use: use
  }, rest, {
    style: computedStyle,
    $cssStyles: cssStyles
  }, domProps, {
    children: /*#__PURE__*/_jsx(Wrapper, {
      children: /*#__PURE__*/_jsx(AlertListTransition, {
        children: renderedAlerts
      })
    })
  }));
  if (use === 'contextual') {
    return renderedAlertList;
  }
  return /*#__PURE__*/_jsx(Layer, {
    "data-layer-for": "FloatingAlertList",
    children: renderedAlertList
  });
};